<template>
  <div class="sales-teams-component">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />
    <DataTable
      class="p-datatable-sm p-datatable-striped p-datatable-gridlines"
      :value="salesTeams"
      :filters.sync="tableStateStore.filters"
      :lazy="true"
      :loading="isLoading"
      :paginator="true"
      :totalRecords="getSalesTeamCount"
      :rows.sync="tableStateStore.pagination.pageSize"
      :first="tableStateStore.skip()"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="
        $t('show') +
          ' {first} ' +
          $t('to') +
          ' {last} ' +
          $t('of') +
          ' {totalRecords} ' +
          $t('records')
      "
      :rowsPerPageOptions="[10, 15, 20, 50, 100, 200]"
      dataKey="number"
      :rowHover="true"
      :stateKey="tableStateStore.filterName()"
      filterDisplay="row"
      :sortField="tableStateStore.sorting.sortField"
      :sortOrder="tableStateStore.sorting.sortOrder"
      stateStorage="local"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left">
            <h1 class="page-title">{{ $t('salesTeams') }}</h1>
          </div>
          <div class="table-header-right">
            <button class="btn btn-sm btn-inverse equal-btn" @click.prevent="onRemoveFilters()">
              <i class="fa fa-remove mr-2"></i>
              {{ $t('remove_filters') }}
            </button>
            <router-link
              class="btn btn-success mb-2 equal-btn"
              :to="{ name: 'SalesTeamCreatePage' }"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('salesTeamCreate') }}
            </router-link>
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>

      <Column
        field="number"
        header="#"
        sortable
        :headerStyle="{ width: '60px' }"
        :bodyStyle="'text-align: center;width: 60px'"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.number === '_new'">
            {{ $t('_new') }}
          </span>
          <span v-else>
            <router-link
              :to="{
                name: 'SalesTeamEditPage',
                params: { number: slotProps.data.number },
              }"
              >{{ slotProps.data.number.toString().padStart(4, '0') }}</router-link
            >
          </span>
        </template>
        <template #filter="{filterModel,filterCallback}">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="name"
        :header="$t('name')"
        sortable
        :showFilterMenu="false"
        :bodyStyle="{ 'text-align': 'left' }"
      >
        <template #body="slotProps">{{ slotProps.data.name }}</template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText type="text" v-model="filterModel.value" @input="filterCallback()" />
        </template>
      </Column>

      <Column
        field="memberCount"
        :header="$t('memberCount')"
        sortable
        :showFilterMenu="false"
        :headerStyle="{ width: '150px', 'text-align': 'right' }"
        :bodyStyle="{ 'text-align': 'right' }"
      >
        <template #body="slotProps">
          {{ slotProps.data.memberCount || 0 }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Breadcrumbs from '@/components/Breadcrumbs';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';

export default {
  name: 'SalesTeamsPage',
  components: {
    Breadcrumbs,
    DataTable,
    Column,
    InputText,
  },
  data() {
    return {
      loading: true,
      salesTeamsTableStateConfig: {
        name: 'salesTeams',
        filters: {
          number: { value: null },
          name: { value: null },
        },
        sorting: { sortField: 'number', sortOrder: 1 },
      },
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getTableState', 'getSalesTeams', 'getSalesTeamCount']),
    tableStateStore() {
      return this.getTableState(this.salesTeamsTableStateConfig.name);
    },
    breadcrumbs() {
      return [{ name: 'Home', route: { name: 'home' } }, { name: this.$t('salesTeams') }];
    },
    salesTeams() {
      return this.getSalesTeams;
    },
  },
  methods: {
    ...mapActions(['initTableState', 'fetchSalesTeamsPaginated']),
    /**
     * Load more results from server or cache on pagination click
     */
    async onPage(event) {
      this.tableStateStore.pagination = { page: event.page, pageSize: event.rows };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on sort
     */
    async onSort(event) {
      this.tableStateStore.sorting = { sortField: event.sortField, sortOrder: event.sortOrder };
      this.loadTableData();
    },
    /**
     * Load results from server/cache on filter
     */
    async onFilter(event) {
      this.loadTableData();
    },
    /**
     * Remove all filters
     */
    async onRemoveFilters(event) {
      this.tableStateStore.reset();
      await this.loadTableData();
    },
    /**
     * Trigger when state is loaded from local storage
     */
    async onStateRestore(event) {
      this.tableStateStore.restore(event);
    },
    /**
     * Load remote table data
     */
    async loadTableData() {
      const filters = {};
      if (this.tableStateStore.filters.number?.value) {
        filters.number = { value: this.tableStateStore.filters.number.value };
      }
      if (this.tableStateStore.filters.name?.value) {
        filters.name = { value: this.tableStateStore.filters.name.value };
      }

      await this.fetchSalesTeamsPaginated({
        pagination: {
          skip: this.tableStateStore.skip(),
          pageSize: this.tableStateStore.pagination.pageSize,
        },
        sorting: this.tableStateStore.sorting,
        filters: filters,
      });
    },
  },
  async created() {
    await this.initTableState(this.salesTeamsTableStateConfig);
    this.loading = false;
  },
  async mounted() {
    await this.loadTableData();
  },
};
</script>

<style scoped lang="scss">
::v-deep .p-column-filter-clear-button {
  display: none;
  width: 0;
}
::v-deep .p-column-filter-menu-button {
  display: none;
  width: 0;
}
/** Fix Dropdown overlay */
::v-deep .p-datatable-responsive-scroll > .p-datatable-wrapper {
  min-height: 75vh;
}
</style>
